import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'

import { runPushEventHandler } from '@/providers/pushEventHandler'
import { PushEvent } from '@domain'
import { useQueryClient } from '@tanstack/react-query'
import AuthUtils from '@utils/AuthUtils'
import useWebSocket, { ReadyState } from 'react-use-websocket'

const socketUrl = import.meta.env.VITE_WS_URL

interface SocketMessage {
	type: string
	payload: unknown
}

const SocketContext = createContext<{
	sendJsonMessage: (msg: SocketMessage) => void
	readyState: ReadyState
} | null>(null)

export function SocketProvider({ children }: { children: ReactNode }) {
	const queryClient = useQueryClient()

	const handleMessage = useCallback(
		(event: MessageEvent) => {
			let message: PushEvent
			try {
				message = JSON.parse(event.data)
			} catch {
				return // ignore non-JSON frames (heartbeats, etc.)
			}
			if (!message?.type) return
			runPushEventHandler({ event: message, queryClient })
		},
		[queryClient]
	)

	const { sendJsonMessage, readyState } = useWebSocket<SocketMessage>(socketUrl, {
		shouldReconnect: () => true,
		reconnectAttempts: 20,
		reconnectInterval: (attemptNumber) => Math.min(1000 * 2 ** attemptNumber, 30000),
		onMessage: handleMessage,
		share: true, // single shared connection across all consumers
		protocols: (() => {
			const token = AuthUtils.getSsoToken()
			return token ? ['bearer', token] : ['bearer']
		})(),
	})

	const value = useMemo(() => ({ sendJsonMessage, readyState }), [sendJsonMessage, readyState])

	// useEffect(() => {
	// 	if (!lastJsonMessage) return
	//
	// 	const { type, payload } = lastJsonMessage
	//
	// 	switch (type) {
	// 		case 'order.updated': {
	// 			const order = payload as { id: string }
	// 			queryClient.setQueryData(['orders', order.id], order)
	// 			break
	// 		}
	// 		// add more cache-update cases as new message types arrive
	// 		default:
	// 			queryClient.invalidateQueries({ queryKey: [type] })
	// 	}
	// }, [lastJsonMessage, queryClient])

	return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocket = () => {
	const ctx = useContext(SocketContext)
	if (!ctx) throw new Error('useSocket must be used within SocketProvider')
	return ctx
}
